import { RefObject } from 'react';
import Highcharts from 'highcharts';
import moment from 'moment';
import Drilldown from 'highcharts/modules/drilldown'; // need to import module specifically so can access drilldown

import store from '../store/store';
import {
  setImagePair,
  setCurrentSelectedImage,
  clearCurrentSelectedImage,
  setTimelineOpen,
  clearCurrentImagePair,
} from '../reducers/auditdata/actions';
import { toggleMapLoader } from '../reducers/mapview/actions';
import { ISentinelLayerResult, ILandsatLayerResult } from '../types/layers';
import { ITimelineUpdateDataObject, IImageryLayer, IDrilldownDataObject, IDateRangeObject } from '../types/timeline';

import config from '../config/config';
import MapController from './MapController';

/** Class used to control the behaviour of a Timeline component  */
export class TimelineController {
  _imageChart: Highcharts.Chart | undefined;
  _landsatData: Array<ILandsatLayerResult> | undefined;
  _sentinelData: Array<ISentinelLayerResult> | undefined;
  _drilledDown: boolean = false;
  _excludeStartDate: boolean = false;
  _cloudCover: number = 100;
  _dateRange?: any;

  constructor() {
    this._imageChart = undefined;
    this._landsatData = undefined;
    this._sentinelData = undefined;
  }

  /**
   * Initialize the timeline chart
   * @param container - the HTML element where we want to render our Highchart
   * @param visibleImageLayers - array of visible layers
   * @param landsatData - optional array of Landsat-8 Data to display in Highcharts
   * @param sentinelData - optional array of Sentinel-2 Data to display in Highcharts
   * @param cloudCover - optional array of cloud cover used to filter rasters
   * @param dateRange
   */
  initializeTimeline = async (
    container: RefObject<any>,
    visibleImageLayers: Array<string>,
    landsatData?: Array<ILandsatLayerResult>,
    sentinelData?: Array<ISentinelLayerResult>,
    cloudCover?: number,
    dateRange?: any
  ) => {
    this._landsatData = landsatData;
    this._sentinelData = sentinelData;
    this._cloudCover = cloudCover || 100;
    this._dateRange = dateRange;
    landsatData = landsatData?.filter((data) =>
      moment(data.acquisitiondate).isBetween(dateRange.startDate, dateRange.endDate, 'day', '[]')
    );
    sentinelData = sentinelData?.filter((data) =>
      moment(data.acquisitiondate).isBetween(dateRange.startDate, dateRange.endDate, 'day', '[]')
    );

    ({ landsatData, sentinelData } = this.filterOnCloudCover(landsatData, sentinelData, cloudCover));

    Drilldown(Highcharts); // Adds the drilldown module to our Highcharts namespace

    Highcharts.setOptions({
      time: {
        useUTC: false,
      },
    });

    this._imageChart = Highcharts.chart(container.current, {
      chart: {
        type: 'scatter',
        zoomType: 'xy',
        height: 150,
        backgroundColor: '#efefef',
        spacingTop: 25,
        events: {
          drilldown: (e) => {
            if (store.getState().mapview.loader) {
              return;
            }
            const originalSeries = e.point.series;
            const markerColor = e.point.y === 1 ? '#1d7ef0' : '#FD3A52';
            if (!e.seriesOptions) {
              const day = moment(e.point.x);
              const data: Array<IDrilldownDataObject> = [];
              originalSeries.data.forEach((point) => {
                if (moment(point.x).isSame(day, 'day')) {
                  // originalDate doesn't exist on HighCharts.Point, so ignore this error
                  //@ts-ignore
                  data.push({ x: point.x, y: point.y, name: point.name, originalDate: point.originalDate });
                  const layer = e.point.y === 1 ? 'sentinel' : 'landsat';
                  //@ts-ignore
                  MapController.toggleBoundingBoxHighlight(point.originalDate, point.name, layer, false);
                }
              });
              if (data.length > 1) {
                const drilldownData: Highcharts.SeriesScatterOptions = {
                  type: 'scatter',
                  name: moment(e.point.x).format('MMMM Do YYYY'),
                  id: e.point.y === 1 ? 'Sentinel' : 'Landsat',
                  color: markerColor,
                  marker: {
                    radius: 8,
                    lineColor: markerColor,
                    lineWidth: 1,
                    symbol: 'circle',
                    states: {
                      hover: {
                        enabled: true,
                        lineColor: '#000000',
                        fillColor: markerColor,
                      },
                      select: {
                        enabled: true,
                        lineColor: '#000000',
                        fillColor: markerColor,
                      },
                    },
                  },
                  data,
                };
                this._drilledDown = true;
                e.target.xAxis[0].setTitle({ text: '' });
                e.target.addSeriesAsDrilldown(e.point, drilldownData);
              }
            }
          },
          drillup: (e) => {
            this._drilledDown = false;
            e.target.xAxis[0].setTitle({ text: '' });
          },
        },
      },
      credits: {
        enabled: false,
      },
      title: {
        text: '',
      },
      xAxis: {
        startOnTick: true,
        endOnTick: true,
        showLastLabel: true,
        type: 'datetime',
        title: {
          y: 10,
        },
        dateTimeLabelFormats: {
          millisecond: '%H:%M:%S',
          second: '%H:%M:%S',
          minute: '%H:%M:%S',
          hour: '%b %e',
          day: '%b %e',
          week: '%b %e',
          month: '%b %Y',
          year: '%b %Y',
        },
      },
      yAxis: {
        showFirstLabel: false,
        endOnTick: false,
        title: {
          text: null,
        },
        labels: {
          formatter: function () {
            return `${this.value === 1 ? 'Sentinel-2' : 'Landsat 8'}`;
          },
        },
      },
      legend: {
        enabled: false,
      },
      exporting: {
        enabled: false,
      },
      plotOptions: {
        scatter: {
          tooltip: {
            headerFormat: '',
            pointFormatter: () => {
              const point = this._imageChart?.hoverPoint;
              if (point) {
                const day = moment(point.x);
                const data = point.series.data.filter((point) => moment(point.x).isSame(day, 'day')); // using moment for comparing as easier than .dateFormat
                if (data.length > 1 && !this._drilledDown) {
                  const dateStrings = data.map((point) => `${Highcharts.dateFormat('%b %e %H:%M:%S', point.x)}<br/>`);
                  return dateStrings.join('');
                } else {
                  return `${
                    this._drilledDown
                      ? Highcharts.dateFormat('%b %e %H:%M:%S', point.x)
                      : Highcharts.dateFormat('%b %e %Y', point.x)
                  }`;
                }
              } else {
                return '';
              }
            },
          },
        },
        series: {
          pointInterval: 30 * 24 * 3600 * 1000, // one month intervals
          point: {
            events: {
              click: (event) => {
                // @ts-ignore
                const { y, name, selected, originalDate } = event.point;
                if (!originalDate || !y || store.getState().mapview.loader) { /// || store.getState().mapview.loader
                  return;
                }
                // COMMENTING OUT UNTIL WE CONFIRM DISABLING OPTION IS THE PREFERRED
                // const selectedImages = store.getState().auditdata.selectedImages;
                // const beforeImage = selectedImages.Before?.imageLayer;
                // const afterImage = selectedImages.After?.imageLayer;
                // if ((beforeImage && beforeImage !== y) || (afterImage && afterImage !== y)) {
                //   alert('MUST SELECT TWO IMAGES FROM THE SAME LAYER');
                //   return;
                // }
                const layerID = IImageryLayer[y];
                const imageID = this.findImage(layerID, originalDate, name);
                if (imageID) {
                  const currentImagePair = store.getState().auditdata.currentImagePair;
                  const isBefore = !currentImagePair.before || (currentImagePair.before && !currentImagePair.before.locked) // It's still a before image if not locked in
                  if (!selected) {
                    const selectedImage = {
                      acquisitionDate: originalDate,
                      imageLayer: y,
                      oid: imageID,
                      highlightID: name,
                      locked: false,
                      cloudcover: this._cloudCover,
                    };
                    store.dispatch(setCurrentSelectedImage(selectedImage, isBefore));
                    store.dispatch(toggleMapLoader(true));
                  } else {
                    store.dispatch(clearCurrentSelectedImage(isBefore));
                  }
                }
                if (event.point.select) {
                  event.point.select(undefined, false); //leaving the first arg as undefined toggles the point as selected/unselected, the second arg is false so we don't accumulate additional points
                }
              },
              mouseOver: (event: any) => {
                // no Typed HC event object here, so have to use any
                const layer = IImageryLayer[event.target.y];
                const day = moment(event.target.x);
                // have to check for drilldown property, which doesn't exist on drilled-down points
                if (event.target.hasOwnProperty('drilldown')) {
                  event.target.series.data.forEach((point: IDrilldownDataObject) => {
                    if (moment(point.x).isSame(day, 'day')) {
                      MapController.toggleBoundingBoxHighlight(point.originalDate, point.name, layer, true, true);
                    }
                  });
                } else {
                  MapController.toggleBoundingBoxHighlight(event.target.originalDate, event.target.name, layer, true, true);
                }
              },
              mouseOut: (event: any) => {
                // no Typed HC event object here, so have to use any
                const layer = IImageryLayer[event.target.y];
                const day = moment(event.target.x);
                if (event.target.hasOwnProperty('drilldown')) {
                  event.target.series.data.forEach((point: IDrilldownDataObject) => {
                    if (moment(point.x).isSame(day, 'day')) {
                      MapController.toggleBoundingBoxHighlight(point.originalDate, point.name, layer, false);
                    }
                  });
                } else {
                  MapController.toggleBoundingBoxHighlight(event.target.originalDate, event.target.name, layer, false);
                }
              },
            },
          },
        },
      },
      series: [
        {
          type: 'scatter',
          name: 'Sentinel-2',
          color: '#1d7ff056',
          marker: {
            radius: 8,
            lineColor: '#1d7ef0',
            lineWidth: 1,
            symbol: 'circle',
            states: {
              hover: {
                enabled: true,
                lineColor: '#000000',
                fillColor: '#1d7ef0',
              },
              select: {
                enabled: true,
                lineColor: '#000000',
                fillColor: '#1d7ef0',
              },
            },
          },
          turboThreshold: config.timeline.timelineTurboThreshold,
          data: sentinelData
            ? this.formatTimelineData(
                sentinelData.map((res: ISentinelLayerResult) => res.acquisitiondate),
                sentinelData.map((res: ISentinelLayerResult) => res.tilename),
                IImageryLayer.sentinel
              )
            : undefined,
          visible: visibleImageLayers.includes('sentinel'),
        },
        {
          type: 'scatter',
          name: 'Landsat 8',
          color: '#fd3a5165',
          marker: {
            radius: 8,
            lineColor: '#FD3A52',
            lineWidth: 1,
            symbol: 'circle',
            states: {
              hover: {
                enabled: true,
                lineColor: '#000000',
                fillColor: '#FD3A52',
              },
              select: {
                enabled: true,
                lineColor: '#000000',
                fillColor: '#FD3A52',
              },
            },
          },
          turboThreshold: config.timeline.timelineTurboThreshold,
          data: landsatData
            ? this.formatTimelineData(
                landsatData.map((res: ILandsatLayerResult) => res.acquisitiondate),
                landsatData.map((res: ILandsatLayerResult) => res.pr),
                IImageryLayer.landsat
              )
            : undefined,
          visible: visibleImageLayers.includes('landsat'),
        },
      ],
      drilldown: {
        series: [],
        drillUpButton: {
          position: {
            y: -20,
          },
        },
      },
    });
  };

  /**
   * Used to filter landsat & sentinel data on cloud coverage
   * @param landsatData The available landsat imagery
   * @param sentinelData The available sentinel imagery
   * @param cloudCover An array of min & max cloud coverage
   */
  filterOnCloudCover = (
    landsatData?: Array<ILandsatLayerResult>,
    sentinelData?: Array<ISentinelLayerResult>,
    cloudCover?: number
  ) => {
    if (cloudCover != null) {
      this._cloudCover = cloudCover;
      landsatData = landsatData?.filter(
        (data) => data.cloudcover != null && data.cloudcover <= Number(cloudCover / 100)
      );
      sentinelData = sentinelData?.filter(
        (data) => data.cloudcover != null && data.cloudcover <= Number(cloudCover / 100)
      );
    }
    return { landsatData, sentinelData };
  };

  /**
   * Format data for use in the timeline chart
   * @param dates - an array of esriFieldTypeDate strings
   * @param ids - an array of PRs or tilenames from Landsat/Sentinel layers
   * @param type - the layer identifier (2: Landsat, 1: Sentinel)
   */
  formatTimelineData = (dates: Array<number>, ids: Array<string | number>, type: number) => {
    const updatedDates = this.updateDates(dates);
    return updatedDates.map((date, index) => {
      return { x: date, y: type, name: ids[index], originalDate: dates[index], drilldown: type === 1 ? 'Sentinel' : 'Landsat' };
    });

  };

  // Function that finds duplicate numbers in an array
  findDuplicates = (array: Array<number>) => {
    return array.filter((item, index) => array.indexOf(item) !== index);
  }

  // Function to update duplicate dates by 1 minute 
  updateDates = (dates: Array<number>) => {
    const newDates = [...dates];
    const duplicates = [...this.findDuplicates(newDates)];
    let itt = 0;
    let date;
    for (let i = 0; i < duplicates.length; i++) {
      // Because not all duplicates are the same, we want to track each set of duplicates and increase respectively
      if (!date || duplicates[i] !== date) {
        itt = 0;
        date = duplicates[i];
      }
      const index = newDates.indexOf(date);
      newDates[index] = date + (1000 * (++itt));
    }
    return newDates;
  }

  /**
   * Get the initial range from all initial data we're using in the Timeline component
   */
  get initialDateRange() {
    return {
      startDate: moment(1363622342000),
      endDate: moment(),
    };
  }

  updateTimelineDates = (dates: IDateRangeObject, excludeStartDate: boolean) => {
    this._dateRange = dates;
    this._excludeStartDate = excludeStartDate;
  }

  filterOnDates = ( //! sentinel is being filtered out here. 
    landsat?: Array<ILandsatLayerResult>,
    sentinel?: Array<ISentinelLayerResult>
  ) => {
    const landsatData = landsat?.filter((data) =>
    // (] = exclude start date
    // [] = include start and end date
    moment(data.acquisitiondate).isBetween(this._dateRange.startDate, this._dateRange.endDate, 'second', this._excludeStartDate ? '(]' : '[]')
    );
    const sentinelData = sentinel?.filter((data) =>
    moment(data.acquisitiondate).isBetween(this._dateRange.startDate, this._dateRange.endDate, 'second', this._excludeStartDate ? '(]' : '[]')
    );
    return { landsatData, sentinelData };
  };

  /***
   * Toggle a Timeline's Imagery Layer as disabled
   * @param selectedLayer - the Imagery Layer title
   */
  filterTimelineDataByLayerType = (visible: boolean, selectedLayer?: string) => {
    const updateObject = {
      visibility: {
        layers: selectedLayer ? [selectedLayer] : ['landsat', 'sentinel'],
        visible: visible,
      },
      cloudCover: this._cloudCover,
      startDate: this._dateRange.startDate,
      endDate: this._dateRange.endDate,
    };
    this.updateTimelineData(updateObject);
  };

  /**
   * Update the Timeline's Highchart - updates can be made to a dataset's visibilty, the range of dates to display data for, or the amount of cloud cover to filter data by
   * @param updateData - an object containing optional values based on what is being updated
   */
  updateTimelineData = (updateData: ITimelineUpdateDataObject) => {
    const { cloudCover, visibility } = updateData;
    let updatedLandsatData;
    let updatedSentinelData;
    let landsatVisibility = this._imageChart?.series[1]?.visible ?? false;
    let sentinelVisibility = this._imageChart?.series[0]?.visible ?? false;

    // if visibility changes, store the new value for setting at the end
    if (visibility) {
      visibility.layers.forEach((layer) => {
        if (layer === 'landsat') {
          landsatVisibility = landsatVisibility !== visibility.visible ? visibility.visible : landsatVisibility;
        } else {
          sentinelVisibility = sentinelVisibility !== visibility.visible ? visibility.visible : sentinelVisibility;
        }
      });
    }

    ({ landsatData: updatedLandsatData, sentinelData: updatedSentinelData } = this.filterOnCloudCover(
      this._landsatData,
      this._sentinelData,
      cloudCover
    ));

    ({ landsatData: updatedLandsatData, sentinelData: updatedSentinelData } = this.filterOnDates(
      updatedLandsatData,
      updatedSentinelData,
    ));

    if (updatedLandsatData) {
      this._imageChart?.series[1]?.setData(
        this.formatTimelineData(
          updatedLandsatData.map((res: ILandsatLayerResult) => res.acquisitiondate),
          updatedLandsatData.map((res: ILandsatLayerResult) => res.pr),
          IImageryLayer.landsat
        ),
        false,
        false
      );
    }

    if (updatedSentinelData) {
      this._imageChart?.series[0]?.setData(
        this.formatTimelineData(
          updatedSentinelData.map((res: ISentinelLayerResult) => res.acquisitiondate),
          updatedSentinelData.map((res: ISentinelLayerResult) => res.tilename),
          IImageryLayer.sentinel
        ),
        false,
        false
      );
    }

    // We have to set visibility last, otherwise we'll get unwanted changes to the visibility whenever the timeline updates
    if (this._imageChart) {
      if  (this._imageChart?.series?.length > 1) {
        this._imageChart?.series[1]?.setVisible(landsatVisibility);
        this._imageChart?.series[0]?.setVisible(sentinelVisibility);
      }
    }
  };

  /**
   * Resets the drill down view when reopening the chart
   */
  resetChartDrillDown = () => {
    this._imageChart?.drillUp();
  };

  /**
   * Function to add selected Timeline images to the map
   */
  addSelectedImagesToMap = () => {
    const imagePairs = store.getState().auditdata.selectedImagePairs.imagePairs;
    const images: Array<number> = [];
    let layerID: string | null = null;
    if (imagePairs.length > 0) {
      imagePairs.forEach((pair) => {
        Object.keys(pair).forEach((key) => {
          if (pair[key]) {
            if (!layerID) {
              layerID = IImageryLayer[pair[key].imageLayer];
            }
            const image = this.findImage(layerID, pair[key].acquisitionDate);
            if (image) {
              images.push(image);
            }
          }
        });
      });
      if (layerID) {
        MapController.addImagesToMap(layerID, images.reverse());
      }
    } else {
      MapController.clearImagesFromMap();
    }
  };

  updateTimelineAndBoundingBoxes = () => {
    const { selectedImagePairs, currentImagePair } = store.getState().auditdata;
    const { imagePairs } = selectedImagePairs;
    let layerToHide, layerID: string;

    if (imagePairs.length > 0) {
      layerID = IImageryLayer[imagePairs[0].before.imageLayer];
      layerToHide = layerID === 'landsat' ? 'sentinel' : 'landsat';
      imagePairs.forEach((pair) => {
        MapController.toggleBoundingBoxHighlight(pair.before.acquisitionDate, pair.before.highlightID, layerID, true);
      });
      MapController.updateBoundingBoxImageVisibility(false, layerToHide);
      this.filterTimelineDataByLayerType(false, layerToHide);
    } else {
      if (currentImagePair.before) {
        layerID = IImageryLayer[currentImagePair.before.imageLayer];
        layerToHide = layerID === 'landsat' ? 'sentinel' : 'landsat';
        MapController.toggleBoundingBoxHighlight(currentImagePair.before.acquisitionDate, currentImagePair.before.highlightID, layerID, true);
        MapController.updateBoundingBoxImageVisibility(false, layerToHide);
        this.filterTimelineDataByLayerType(false, layerToHide);
      } else {
        MapController.updateBoundingBoxImageVisibility(true);
        this.filterTimelineDataByLayerType(true);
      }
    }
  };

  /**
   * Function to return chosen image
   * @param imageLayer - Name of the image layer we're searching on
   * @param date - Date to search by
   */
  findImage = (imageLayer: string, date: number, id?: string) => {
    const layerID = imageLayer === 'landsat' ? 'landsat' : 'sentinel';
    if (layerID === 'landsat' && this._landsatData) {
      const image = this._landsatData?.find((res: ILandsatLayerResult) => res.acquisitiondate === date);
      if (image) {
        return image.objectid;
      }
    } else if (layerID === 'sentinel' && this._sentinelData) {
      const image = this._sentinelData?.find((res: ISentinelLayerResult) => res.tilename === id && res.acquisitiondate === date);
      if (image) {
        return image.objectid;
      }
    }
  };

  /**
   * Set the selected image and persist it in Redux
   */
  saveSelectedImage = () => {
    const { before, after } = store.getState().auditdata.currentImagePair;
    if (before && before.locked && after) {
      // We don't need after to be locked, as this is fired when a user locks in the after image
      store.dispatch(setImagePair({ before, after }));
      store.dispatch(clearCurrentImagePair());
      store.dispatch(setTimelineOpen(false));
      return;
    }
    if (before && !before.locked) {
      const lockedBefore = { ...before };
      lockedBefore.locked = true;
      store.dispatch(setCurrentSelectedImage(lockedBefore, true));
      return;
    }
  };

  /**
   * method to destory timeline when we delete audit area
   */
  destroyChart = () => {
    this._imageChart?.destroy();
    this._imageChart = undefined;
  };
}

const timelineController = new TimelineController();

//TODO: This is for DEV only and should be removed once we deploy to prod
declare global {
  interface Window {
    timelineController: typeof timelineController;
  }
}

window.timelineController = timelineController;

export default timelineController;
